import { WEDGE_SHELL as B } from '@territory/tokens';
import { control, font, radius, space, TOUCH_MIN, weight } from '../scale.js';

export type WedgeAuthSize = 'touch' | 'recovery' | 'compact' | 'native' | 'confirmation' | 'request' | 'form-back' | 'native-link' | 'native-back' | 'copy' | 'plain-link' | 'route';
export type WedgeAuthSurface = 'flat';
export type WedgeAuthMotion = 'instant' | 'color';
export type WedgeAuthHover = 'quiet' | 'none';
const sizes: Record<string, readonly WedgeAuthSize[]> = {
  primary: ['touch', 'recovery', 'compact', 'native', 'route'],
  secondary: ['confirmation', 'request', 'copy', 'route'],
  ghost: ['form-back', 'native-link', 'native-back', 'plain-link'],
};
export function authButtonOptions(variant: string, size?: WedgeAuthSize, motion?: WedgeAuthMotion, hover?: WedgeAuthHover, surface?: WedgeAuthSurface) {
  return {
    size: size && sizes[variant]?.includes(size) ? size : undefined,
    motion: motion === 'instant' || (motion === 'color' && variant === 'ghost') ? motion : undefined,
    surface: surface === 'flat' && variant === 'primary' ? surface : undefined,
    hover: hover === 'none' || (hover === 'quiet' && variant === 'secondary') ? hover : undefined,
  };
}

/* Exact emeraldBtn, glassBtn and linkBtn from wedgeBrand.tsx. Native control layout and focus stay intact. */
const ROOT = '[data-rv-button][data-rv-appearance="wedge-auth"]';
const primary = `${ROOT}[data-rv-variant="primary"]`;
const secondary = `${ROOT}[data-rv-variant="secondary"]`;
const link = `${ROOT}[data-rv-variant="ghost"]`;
/* Released helper geometry, off-scale. */
const PRIMARY_SIZE = 14.5;
const PRIMARY_PAD = 14;
const SECONDARY_PAD = 13;
const LINK_MARGIN = 10;
const RECOVERY_PY = 13;
const RECOVERY_PX = 26;
const TRANSITION_MS = 200;
/* Released entry and confirmation geometry, off-scale. */
const COMPACT_SIZE = 13.5;
const COMPACT_PX = 22;
const CONFIRMATION_PY = 11;
const SMALL_PY = 10;
const REQUEST_PX = 18;
const BACK_PY = 6;
const hoverAllowed = ':not(:where([data-rv-auth-hover="none"]))';
const regularGlass = ':not(:where([data-rv-auth-hover="quiet"], [data-rv-auth-hover="none"]))';
const transition = `all ${TRANSITION_MS}ms ${B.ease}`;
export const WEDGE_AUTH_BUTTON_CSS = `${ROOT} { width: 100%; font-family: inherit; cursor: pointer; }
${primary} { padding: ${PRIMARY_PAD}px; border-radius: ${radius.xl}px; border: 0; background: ${B.accent}; color: ${B.accentInk}; font-size: ${PRIMARY_SIZE}px; font-weight: ${weight.bold}; transition: ${transition}; box-shadow: ${B.ctaShadow}; }
${primary}[data-rv-auth-surface="flat"] { box-shadow: none; }
${primary}:disabled { background: ${B.ctaBusy}; cursor: default; box-shadow: none; }
${primary}:hover:not(:disabled)${hoverAllowed} { filter: brightness(1.07); transform: translateY(-1px); box-shadow: ${B.ctaHoverShadow} !important; }
${secondary} { padding: ${SECONDARY_PAD}px; border-radius: ${radius.xl}px; border: 1px solid ${B.border}; background: ${B.surface}; color: ${B.text}; font-size: ${font.body}px; font-weight: ${weight.semibold}; transition: ${transition}; }
${secondary}:hover:not(:disabled)${regularGlass} { background: ${B.glassHover} !important; border-color: ${B.glassHoverBorder} !important; transform: translateY(-1px); }
${link} { margin-top: ${LINK_MARGIN}px; background: none; border: 0; color: ${B.textSec}; font-size: ${font.footnote}px; font-weight: ${weight.semibold}; }
${link}:not(:where([data-rv-auth-size="plain-link"])) { padding: ${space[2]}px; }
${link}:hover${hoverAllowed} { color: ${B.accent} !important; }
/* Released ResetRedeem and MagicRedeem geometry and disabled cursors. */
${primary}[data-rv-auth-size="touch"], ${primary}[data-rv-auth-size="recovery"] { min-height: ${TOUCH_MIN}px; }
${primary}[data-rv-auth-size="recovery"] { padding: ${RECOVERY_PY}px ${RECOVERY_PX}px; }
${primary}:disabled[data-rv-auth-state="invalid"] { cursor: not-allowed; }
${primary}:disabled[data-rv-auth-state="pending"] { cursor: wait; }
/* Released auth entry, confirmation and back controls. Width and margin placement stay caller-owned. */
${primary}[data-rv-auth-size="compact"] { padding: ${space[3]}px ${COMPACT_PX}px; font-size: ${COMPACT_SIZE}px; }
${primary}[data-rv-auth-size="native"] { min-height: ${control.field}px; }
${secondary}[data-rv-auth-size="confirmation"] { padding: ${CONFIRMATION_PY}px ${space[6]}px; font-size: ${COMPACT_SIZE}px; }
${secondary}[data-rv-auth-size="request"] { padding: ${SMALL_PY}px ${REQUEST_PX}px; font-size: ${font.footnote}px; }
${secondary}[data-rv-auth-size="copy"] { padding: ${SMALL_PY}px ${space[5]}px; font-size: ${font.footnote}px; }
${secondary}[data-rv-auth-hover="quiet"]:hover:not(:disabled) { background: ${B.glassQuietHover} !important; border-color: ${B.borderStrong} !important; }
${link}[data-rv-auth-size="form-back"] { padding: ${BACK_PY}px ${space[2]}px; }
${link}[data-rv-auth-size="form-back"]:disabled { cursor: default; }
${link}[data-rv-auth-size="native-link"], ${link}[data-rv-auth-size="native-back"] { padding: ${SMALL_PY}px ${space[2]}px; min-height: ${TOUCH_MIN}px; }
${link}[data-rv-auth-size="native-back"]:disabled { cursor: default; }
/* Released WedgeLoadError controls. Width, tracking and native minimum-height omission are part of this look. */
${ROOT}[data-rv-auth-size="route"] { width: auto; padding: ${SMALL_PY}px ${space[5]}px; font-size: ${font.footnote}px; letter-spacing: 0.3px; }
/* The omitted transition computes to all/0s/ease. Preserve that value rather than transition-property none. */
${ROOT}[data-rv-auth-motion="instant"] { transition: initial; }
${link}[data-rv-auth-motion="color"] { transition: color ${TRANSITION_MS}ms ease; }`;
